import { useCallback, useState } from 'react';
import {
    ensureReaderWarm,
    requestTapToPayLocationPermission,
    setTapToPayProgressListener,
} from '@/lib/tapToPay';
import { showTapToPayEducation } from '@/lib/tapToPayEducation';

export type TapToPaySetupStatus = 'idle' | 'running' | 'done' | 'error';

/**
 * The one-time per-device Tap to Pay setup: grant location, then connect a
 * reader once so Apple's provisioning and Terms acceptance happen here instead
 * of during a live payment. That first connect takes upwards of 45 seconds and
 * cannot be made faster — only moved off the path where a customer is waiting.
 *
 * Safe to run repeatedly: `ensureReaderWarm` is idempotent and joins whatever
 * warm is already in flight.
 */
export function useTapToPaySetup(terminalLocationId?: string) {
    const [status, setStatus] = useState<TapToPaySetupStatus>('idle');
    const [message, setMessage] = useState('');

    const runSetup = useCallback(async () => {
        setStatus('running');
        setMessage('Preparing Tap to Pay…');

        // Take over progress reporting for the duration, then hand it back —
        // a payment screen may already be listening.
        const previousListener = setTapToPayProgressListener((update) => setMessage(update.message));
        try {
            await requestTapToPayLocationPermission();
            await ensureReaderWarm(terminalLocationId);
            setStatus('done');
            setMessage('This device is ready to accept Tap to Pay.');

            // §4.1: education must follow Terms acceptance, which the connect above
            // triggers. iOS 18+ is required, so Apple's ProximityReaderDiscovery
            // screens are always available — no fallback needed. After setStatus so
            // education failing can never make a successful setup look failed.
            void showTapToPayEducation();
        } catch (err: any) {
            setStatus('error');
            setMessage(err?.message ?? 'Tap to Pay setup failed. Please try again.');
        } finally {
            setTapToPayProgressListener(previousListener);
        }
    }, [terminalLocationId]);

    return { status, message, runSetup };
}
